import type { NavItem, NavDrillGroup } from '../NavBar/nav-types';
import {
  CONFIGURE_HUB_ROOTS,
  CONFIGURE_SECTION_MAP,
  HIDDEN_CONFIGURE_PATHS,
  orgRelativePath,
} from '../ConfigureShell/configure-sections';

/**
 * Extract the Connect sidebar items from the configure-surface drill groups.
 *
 * Items are identified by their org-relative path falling under
 * `/configure/connect/`. Label overrides and hub-root rewrites from the
 * configure-sections mapping table are applied so the items render identically
 * to how they appeared in Configure's "Connect" group.
 */
export function buildConnectNav(
  slug: string,
  configureDrillGroups: readonly NavDrillGroup[],
): NavItem[] {
  const items: NavItem[] = [];
  const prefix = '/configure/connect/';

  for (const group of configureDrillGroups) {
    // Sub-drills (e.g. Integrations per-provider) are detail pages, not nav rows.
    if (group.parentBasePath) continue;

    for (const item of group.items) {
      const relative = orgRelativePath(item.to, slug);
      if (!relative.startsWith(prefix)) continue;
      if (HIDDEN_CONFIGURE_PATHS.has(relative)) continue;
      // Per-provider integration detail pages and plugin config pages stay out.
      if (relative.startsWith('/configure/connect/integrations/')) continue;
      if (relative.startsWith('/configure/connect/plugins/')) continue;

      const mapping = CONFIGURE_SECTION_MAP[relative];
      const hubRoot = CONFIGURE_HUB_ROOTS[relative];
      items.push({
        ...item,
        ...(hubRoot ? { to: `/orgs/${slug}${hubRoot}` } : {}),
        ...(mapping?.label ? { label: mapping.label } : {}),
        ...(mapping?.description ? { description: mapping.description } : {}),
      });
    }
  }

  return items;
}
